import mapboxgl from 'mapbox-gl'
import Layer from '@/components/geoview/Layer'
import CreateTool from '@/components/geoview/tools/Create'
import MeasureTool from '@/components/geoview/tools/Measure'
import PointerTool from '@/components/geoview/tools/Pointer'
import DrawTool from '@/components/geoview/tools/Draw'
import BaseTool from '@/components/geoview/tools/BaseTool'
import { buildSearchString } from '@/helper/Functions'
import store from '@/store'
import _ from 'lodash'

// import SphericalMercator from '@mapbox/sphericalmercator'

class MapHelper {
  constructor ({ mapId, zoom, bounds, activeTool, onLoad, onToolInfo, onBoundsUpdate, onZoomUpdate, onObjectsSelect, onToolError, onToolSave, $dialog, transformRequest } = {}) {
    this.layers = {}
    this.activeTool = null
    this.bounds = bounds
    this.zoom = zoom
    this.$dialog = $dialog
    this.onLoad = onLoad
    this.onObjectsSelect = onObjectsSelect
    this.onToolSave = onToolSave
    this.onToolError = onToolError
    this.onToolInfo = onToolInfo
    this.onBoundsUpdate = onBoundsUpdate
    this.onZoomUpdate = onZoomUpdate
    this.transformRequest = transformRequest
    this.localActiveObjects = []
    this.localWaitingActiveObjects = []

    let mapBoxOptions = {
      container: mapId,
      style: 'mapbox://styles/mapbox/streets-v11', // satellite-streets-v11
      center: [9.2331534, 50.9110325],
      transformRequest: this.transformRequest
    }

    if (this.bounds instanceof Array && this.bounds.length > 0) {
      mapBoxOptions.bounds = this.bounds
    }
    if (this.zoom) {
      mapBoxOptions.zoom = this.zoom
    }

    this.map = new mapboxgl.Map(mapBoxOptions)

    this.setActiveTool(activeTool)

    this.map.on('load', this.onMapLoad)
    this.map.on('moveend', this.onMoveEnd)
    this.map.on('zoomend', this.onZoomEnd)
    this.map.on('datalaoding', this.onDataLoading)
    this.map.on('data', this.onSourceDataLoading)
    this.map.on('click', this.onClick)
    this.map.on('error', this.onError)

    /** events:
     * moveend -> bounds updaten
     * zoomend -> zoom updaten
     * datalaoding?
     * sourcedataloading
     */
  }

  setMapStyle (value, beforeLayer) {
    // alte logik, um alle layer neu hinzuzufügen und zu sortieren
    /* for (let key in this.layers) {
      if (this.layers.hasOwnProperty(key)) {
        this.layers[key].remove()
      }
    }

    let mapboxUpdate = () => {
      // after a style switch, we need to add all layers again
      for (let key in this.layers) {
        if (this.layers.hasOwnProperty(key)) {
          this.layers[key].addLayerAfterStyleChange(this.bounds)
        }
      }

      // and now sort them
      for (let key in this.layers) {
        if (this.layers.hasOwnProperty(key)) {
          this.moveLayer(this.layers[key], this.layers[key].beforeLayer)
        }
      }
      this.map.off('style.load', mapboxUpdate)
    }

    this.map.on('style.load', mapboxUpdate) */

    switch (value) {
      case 'satellite':
        this.map.addLayer({
          id: 'satellite',
          source: { 'type': 'raster', 'url': 'mapbox://mapbox.satellite', 'tileSize': 256 },
          type: 'raster'
        }, this.layers[beforeLayer.id].getLayerId())
        break
      default:
        if (this.map.getLayer('satellite')) {
          this.map.removeLayer('satellite')
          this.map.removeSource('satellite')
        }
      // this.map.setStyle('mapbox://styles/mapbox/streets-v11')
    }
  }

  addLayer (layer, { searchParams, mode }, beforeLayer) {
    const newLayer = new Layer(layer, this.map)
    if (beforeLayer) {
      newLayer.allowHoverState = false
      newLayer.setBeforeLayer(beforeLayer)
      newLayer.addLayerToMap({ filter: { bounds: this.bounds }, searchParams, mode }, this.layers[beforeLayer.id])
    } else {
      newLayer.allowHoverState = true
      newLayer.setBeforeLayer(undefined)
      newLayer.addLayerToMap({ filter: { bounds: this.bounds }, searchParams, mode })
    }
    this.layers[layer.id] = newLayer

    return newLayer
  }

  moveLayer (layer, beforeLayer) {
    if (beforeLayer) {
      this.layers[layer.id].allowHoverState = false
      this.layers[layer.id].setBeforeLayer(beforeLayer)
      this.map.moveLayer(this.layers[layer.id].getLayerId(), this.layers[beforeLayer.id].getLayerId())
    } else {
      this.layers[layer.id].setBeforeLayer(undefined)
      this.layers[layer.id].allowHoverState = true
      this.map.moveLayer(this.layers[layer.id].getLayerId())
    }
  }

  removeLayer (layer) {
    if (this.layers[layer.id] instanceof Layer) {
      this.layers[layer.id].remove()
      delete this.layers[layer.id]
    }
  }

  updateLayerSources ({ filter, searchParams, mode }) {
    for (let layerId in this.layers) {
      if (!this.layers.hasOwnProperty(layerId)) {
        return
      }

      if (this.layers[layerId].type === 'wms') {
        return
      }

      this.map.getSource(this.layers[layerId].getSourceId()).setData(this.layers[layerId].getDataUrl() + buildSearchString({
        filter,
        searchParams,
        mode
      }))
    }
  }

  setActiveTool (value) {
    if (this.activeTool instanceof BaseTool) {
      this.activeTool.destroy()
    }
    switch (value) {
      case 'create':
        this.activeTool = new CreateTool(this)
        break
      case 'draw':
        this.activeTool = new DrawTool(this)
        break
      case 'measure':
        this.activeTool = new MeasureTool(this)
        break
      case 'pointer':
      default:
        this.activeTool = new PointerTool(this)
    }
  }

  setCenter (coordinates) {
    this.map.flyTo({ center: coordinates })
  }

  selectedFeatures (objects) {
    let handled = []
    for (let i = 0; i < objects.length; i++) {
      handled.push(objects[i].id)
      if (objects[i].source) {
        // war oder ist ein mapbox feature
        this.map.setFeatureState({ source: objects[i].source, id: objects[i].id }, { selected: true })
        this.localActiveObjects.push(objects[i])
      } else {
        objects[i].source = objects[i].layer + 'Source'

        if (this.map.getSource(objects[i].source)) {
          this.localActiveObjects.push(objects[i])
          this.map.setFeatureState({ source: objects[i].source, id: objects[i].id }, { selected: true })
        } else {
          this.localWaitingActiveObjects.push(objects[i])
        }
      }
    }

    // löschungen
    let difference = this.localActiveObjects.filter(item => handled.indexOf(item.id) === -1)
    difference.forEach(item => this.map.setFeatureState({ source: item.source, id: item.id }, { selected: false }))
    difference = this.localWaitingActiveObjects.filter(item => handled.indexOf(item.id) === -1)
    difference.forEach(item => this.map.setFeatureState({ source: item.source, id: item.id }, { selected: false }))
  }

  updateActivetool (data) {
    this.activeTool.update(data)
  }

  onMapLoad = (e) => {
    // get the current bounds in case we dont have our initial bounds
    const bounds = e.target.getBounds()
    this.bounds = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]
    if (typeof this.onBoundsUpdate === 'function') {
      this.onBoundsUpdate(this.bounds)
    }

    if (typeof this.onLoad === 'function') {
      this.onLoad(e)
    }
  }

  onMoveEnd = (e) => {
    const bounds = e.target.getBounds()
    this.bounds = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]
    if (typeof this.onBoundsUpdate === 'function') {
      this.onBoundsUpdate(this.bounds)
    }
  }

  onZoomEnd = (e) => {
    if (typeof this.onZoomUpdate === 'function') {
      this.onZoomUpdate(e.target.getZoom())
    }
  }

  onDataLoading = (e) => {
  }

  onSourceDataLoading = (e) => {
    if (e.dataType === 'source' && e.isSourceLoaded === true) {
      if (this.localWaitingActiveObjects.length > 0) {
        let activatableObjects = this.localWaitingActiveObjects.filter((object) => object.source === e.sourceId)
        for (let i = 0; i < activatableObjects.length; i++) {
          this.map.setFeatureState({ source: activatableObjects[i].source, id: activatableObjects[i].id }, { selected: true })
          this.localActiveObjects.push(activatableObjects[i])
          this.localWaitingActiveObjects.splice(_.findIndex(this.localWaitingActiveObjects, (data) => data.id === activatableObjects[i].id), 1)
        }
      }
    }
  }

  onError = (e) => {
    if (typeof e.error.url !== 'undefined' && e.error.url.startsWith(process.env.VUE_APP_API_ENDPOINT) && (e.error.status === 401 || e.error.status === 503)) {
      store.dispatch('auth/sessionLogout')
    }
  }

  onClick = async (e) => {
    return this.activeTool.onClick(e)
  }

  resetSelection () {
    if (typeof this.activeTool.resetSelection === 'function') {
      this.activeTool.resetSelection()
    }
  }
}

export default MapHelper
